import React, { useMemo } from "react";
import {
  WSAvatar,
  WSAvatarProps,
  WSElement,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import styles from "./styles.module.scss";
import { StepPoint } from "../Layout/StepPoint";

export type StepItem = {
  title: string;
  status:
    | "Pending"
    | "Active"
    | "InProgress"
    | "Complete"
    | "Warning"
    | "Failed"
    | "Rejected"
    /**
     * Reached a terminal state, but not a bad one — the step stopped rather than
     * failed. Rendered grey so it reads as "no longer applicable" instead of
     * borrowing the red of `Rejected`/`Failed`.
     */
    | "Cancelled";
};
export type StepsProps = {
  steps: StepItem[];
} & WSMarginProps;
// TODO: Maybe move this to a shared component in the future
export const Steps: React.FC<StepsProps> = ({ steps }) => {
  return (
    <WSElement className={styles.steps}>
      {steps.map((step, index) => (
        <Step key={index} {...step} />
      ))}
    </WSElement>
  );
};
const Step: React.FC<StepItem> = ({ title, status }) => {
  const avatarProps = useMemo(() => {
    const statusToPropsMap: Record<StepItem["status"], WSAvatarProps> = {
      Pending: { type: "icon", color: "gray200", icon: "time" }, // not required, we will render StepPoint instead
      Active: { type: "icon", color: "blue500", icon: "time" }, // not required, we will render StepPoint--active instead
      InProgress: { type: "icon", color: "blue500", icon: "time" },
      Complete: { type: "icon", color: "green400", icon: "check" },
      Warning: { type: "icon", color: "amber300", icon: "alert" },
      Failed: { type: "icon", color: "red300", icon: "alert-circle" },
      Rejected: { type: "icon", color: "red300", icon: "exit-circle" },
      Cancelled: { type: "icon", color: "gray300", icon: "exit-circle" }
    };
    return statusToPropsMap[status];
  }, [status]);

  return (
    <WSElement className={styles.step}>
      <WSText.ParagraphXs
        className={styles.title}
        weight="medium"
        color="gray600"
      >
        {title}
      </WSText.ParagraphXs>
      {["Pending", "Active"].includes(status) ? (
        <StepPoint className={styles.point} isActive={status === "Active"} />
      ) : (
        <WSAvatar
          className={styles.point}
          size="S"
          format="circle"
          {...avatarProps}
          colorBackground={avatarProps.color}
          color="white"
        />
      )}
    </WSElement>
  );
};
